import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PartyPopper, X, Loader2, Building2 } from 'lucide-react';
import confetti from 'canvas-confetti';

const US_STATES: { code: string; name: string }[] = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],
  ['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],
  ['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],
  ['PA','Pennsylvania'],['PR','Puerto Rico'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['VI','U.S. Virgin Islands'],['WA','Washington'],['WV','West Virginia'],
  ['WI','Wisconsin'],['WY','Wyoming'],
  // Additional U.S. territories
  ['AS','American Samoa'],['GU','Guam'],['MP','Northern Mariana Islands'],
].map(([code, name]) => ({ code, name }));

const schema = z.object({
  dba: z.string().trim().min(1, 'Company name is required').max(200),
  physical_address_line1: z.string().trim().min(1, 'Address is required').max(200),
  physical_city: z.string().trim().min(1, 'City is required').max(100),
  physical_state: z.string().regex(/^[A-Z]{2}$/, 'Select a state'),
  physical_postal_code: z.string().trim().regex(/^\d{5}(-\d{4})?$/, 'Enter a valid ZIP (12345 or 12345-6789)'),
  ein: z.string().trim().regex(/^\d{2}-\d{7}$/, 'EIN must be XX-XXXXXXX'),
  duns_number: z.string().trim().regex(/^\d{9}$/, 'DUNS must be 9 digits').optional().or(z.literal('')),
});

export const ShipperBasicInfoGate: React.FC = () => {
  const { user, profile, refreshProfile } = useAuth();

  const [dba, setDba] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [ein, setEin] = useState('');
  const [duns, setDuns] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const confettiFired = React.useRef(false);

  // Pre-fill from previously-saved shipper_credit_profiles.dba only.
  // Never pre-fill from companies.name (often contains personal first name).
  const prefilled = React.useRef(false);
  useEffect(() => {
    if (prefilled.current || !user?.id) return;
    prefilled.current = true;
    (async () => {
      const { data: membership } = await supabase
        .from('company_users')
        .select('company_id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('is_primary', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!membership?.company_id) return;
      const { data: existing } = await supabase
        .from('shipper_credit_profiles')
        .select('dba')
        .eq('company_id', membership.company_id)
        .maybeSingle();
      if (existing?.dba && existing.dba.trim() && !dba) setDba(existing.dba);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Format EIN as user types (digits → XX-XXXXXXX, max 9 digits).
  const handleEinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 2) setEin(digits);
    else setEin(`${digits.slice(0, 2)}-${digits.slice(2)}`);
  };

  // DUNS: digits only, max 9.
  const handleDunsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDuns(e.target.value.replace(/\D/g, '').slice(0, 9));
  };

  // ZIP: digits + optional dash, max 10 chars.
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d-]/g, '').slice(0, 10);
    setZip(raw);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    const parsed = schema.safeParse({
      dba,
      physical_address_line1: address,
      physical_city: city,
      physical_state: state,
      physical_postal_code: zip,
      ein,
      duns_number: duns,
    });

    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      parsed.error.issues.forEach((i) => {
        const key = i.path[0] as string;
        if (!fieldErrors[key]) fieldErrors[key] = i.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setSaving(true);
    try {
      // Resolve primary company.
      const { data: membership, error: memErr } = await supabase
        .from('company_users')
        .select('company_id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('is_primary', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (memErr) throw memErr;
      if (!membership?.company_id) throw new Error('No company found for this account.');

      // Upsert credit profile.
      const payload = {
        company_id: membership.company_id,
        dba: parsed.data.dba,
        physical_address_line1: parsed.data.physical_address_line1,
        physical_city: parsed.data.physical_city,
        physical_state: parsed.data.physical_state,
        physical_postal_code: parsed.data.physical_postal_code,
        ein: parsed.data.ein,
        duns_number: parsed.data.duns_number || null,
        physical_country: 'US',
      };
      const { data: upRow, error: upErr } = await supabase
        .from('shipper_credit_profiles')
        .upsert(payload, { onConflict: 'company_id' })
        .select('company_id')
        .maybeSingle();
      if (upErr) throw upErr;
      if (!upRow?.company_id) throw new Error('Save did not persist. Please try again.');

      // Mirror company name + address/phone/email into public.companies via
      // SECURITY DEFINER RPC. Address fields are COALESCE-only; the name is
      // written only when companies.name is null/blank or still the
      // personal-name placeholder (never clobbers a real name).
      // Best-effort: mirror is auxiliary; do not fail onboarding if it errors.
      try {
        const { data: profRow } = await supabase
          .from('profiles')
          .select('email, phone, phone_verified')
          .eq('id', user.id)
          .maybeSingle();
        const { error: mirrorErr } = await supabase.rpc('sync_shipper_company_basics', {
          p_company_id: membership.company_id,
          p_company_name: parsed.data.dba,
          p_address_line1: parsed.data.physical_address_line1,
          p_city: parsed.data.physical_city,
          p_state: parsed.data.physical_state,
          p_postal_code: parsed.data.physical_postal_code,
          p_country: 'US',
          p_email: profRow?.email ?? null,
          p_phone: profRow?.phone_verified ? (profRow?.phone ?? null) : null,
        });
        if (mirrorErr) console.warn('[ShipperBasicInfoGate] company mirror failed:', mirrorErr.message);
      } catch (mirrorEx: any) {
        console.warn('[ShipperBasicInfoGate] company mirror exception:', mirrorEx?.message || mirrorEx);
      }

      // Flip the documents_submitted flag so this gate stops firing.
      const { error: profErr } = await supabase
        .from('profiles')
        .update({ documents_submitted: true })
        .eq('id', user.id);
      if (profErr) throw profErr;

      setShowWelcome(true);
    } catch (err: any) {
      console.error('[ShipperBasicInfoGate] submit error:', err?.message || err);
      setErrors({ _form: err?.message || 'Could not save. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Confetti when welcome modal opens.
  useEffect(() => {
    if (!showWelcome || confettiFired.current) return;
    confettiFired.current = true;
    const duration = 2000;
    const end = Date.now() + duration;
    const frame = () => {
      confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#00e5ff', '#02857E', '#22c55e'] });
      confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#00e5ff', '#02857E', '#22c55e'] });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, [showWelcome]);

  const handleDismissWelcome = async () => {
    await refreshProfile();
    // Defensive: confirm (up to 2s) that the DB actually reports
    // documents_submitted=true before unmounting the welcome. Guarantees the
    // parent DocumentUploadGate re-evaluates with the fresh flag on its very
    // next render and returns null cleanly — no blank flash, no re-render of
    // the empty form. Root cause (transient null profile during refresh) is
    // fixed in AuthContext.refreshProfile; this is a belt-and-suspenders guard.
    if (user?.id) {
      const start = Date.now();
      while (Date.now() - start < 2000) {
        const { data } = await supabase
          .from('profiles')
          .select('documents_submitted')
          .eq('id', user.id)
          .maybeSingle();
        if (data?.documents_submitted) break;
        await new Promise((r) => setTimeout(r, 150));
      }
    }
    setShowWelcome(false);
  };

  if (showWelcome) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="absolute inset-0 bg-background/10 backdrop-blur-[1px]" />
        <div className="relative w-full max-w-md mx-4 rounded-2xl border bg-card shadow-2xl p-8 flex flex-col items-center text-center gap-5 animate-in fade-in zoom-in-95 duration-300">
          <button
            onClick={handleDismissWelcome}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-16 h-16 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center">
            <PartyPopper className="w-8 h-8 text-accent" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground mb-2">Welcome to LaneRunner! 🎉</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your company info is saved. You can complete the full credit profile any time
              from Company Settings → Credit Profile.
            </p>
          </div>
          <Button onClick={handleDismissWelcome} className="w-full h-11 text-sm font-semibold" size="lg">
            Let's go →
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto py-8">
      <div className="absolute inset-0 bg-background/10 backdrop-blur-[1px]" />
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-lg mx-4 rounded-2xl border bg-card shadow-2xl p-6 flex flex-col gap-4 animate-in fade-in zoom-in-95 duration-300"
      >
        <div className="absolute -top-px left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-accent to-transparent" />

        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center mb-3">
            <Building2 className="w-7 h-7 text-accent" />
          </div>
          <h2 className="text-xl font-bold text-foreground">A few quick company details</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Brokers will see this when you post loads. Takes about a minute.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <Field label="Company name (Trade name / DBA)" required error={errors.dba}>
            <Input value={dba} onChange={(e) => setDba(e.target.value)} maxLength={200} placeholder="Acme Foods LLC" autoFocus />
          </Field>

          <Field label="Physical address" required error={errors.physical_address_line1}>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} maxLength={200} placeholder="123 Main St" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="City" required error={errors.physical_city}>
              <Input value={city} onChange={(e) => setCity(e.target.value)} maxLength={100} placeholder="Chicago" />
            </Field>
            <Field label="State" required error={errors.physical_state}>
              <Select value={state} onValueChange={setState}>
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {US_STATES.map((s) => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.code} — {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="ZIP" required error={errors.physical_postal_code}>
              <Input value={zip} onChange={handleZipChange} placeholder="60601" inputMode="numeric" />
            </Field>
            <Field label="EIN (XX-XXXXXXX)" required error={errors.ein}>
              <Input value={ein} onChange={handleEinChange} placeholder="12-3456789" inputMode="numeric" />
            </Field>
          </div>

          <Field label="DUNS number (optional)" error={errors.duns_number}>
            <Input value={duns} onChange={handleDunsChange} placeholder="123456789" inputMode="numeric" maxLength={9} />
          </Field>
        </div>

        {errors._form && (
          <p className="text-sm text-destructive">{errors._form}</p>
        )}

        <Button
          type="submit"
          disabled={saving}
          className="w-full h-11 text-sm font-semibold"
          size="lg"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
            </>
          ) : (
            'Save & continue'
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          You can complete the full credit profile later from Company Settings.
        </p>
      </form>
    </div>
  );
};

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

function Field({ label, required, error, children }: FieldProps) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground mb-1.5 block">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

export default ShipperBasicInfoGate;
